import React, { useEffect, useMemo, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  Link,
  useNavigate,
  useParams,
  useLocation
} from 'react-router-dom';

import { getPageHelp } from './pageHelp';
import { firstAllowedPath, hasPermission } from './permissions';

// Komponent do ochrony tras wymagających uwierzytelnienia
function RequireAuth({ children, role }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Sprawdź role użytkownika jeśli wymagana
  if (role && user && user.role !== 'admin' && role === 'admin') {
    return <Navigate to="/brak-dostepu" replace />;
  }

  return children;
}

// Komponent do ochrony tras wymagających konkretnego uprawnienia
function RequirePermission({ permission, children }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Admins always have all permissions
  if (user && user.role === 'admin') {
    return children;
  }

  // Check if user has the required permission
  if (!hasPermission(user, permission)) {
    return <Navigate to="/brak-dostepu" replace />;
  }

  return children;
}

// Komponent do obsługi braku dostępu
function NoAccess() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [helpOpen, setHelpOpen] = useState(false);
  const help = useMemo(() => getPageHelp('no-access'), []);
  const targetPath = useMemo(() => firstAllowedPath(user), [user]);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [helpOpen]);
  
  return (
    <div className="loginPage">
      <div className="card" style={{ maxWidth: '400px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button type="button" className="secondary" onClick={() => setHelpOpen(true)}>ℹ️ Info</button>
        </div>
        <h2>Brak dostępu</h2>
        <p>Nie masz uprawnień do przeglądania tej sekcji.</p>
        <button onClick={() => navigate(targetPath)} className="btn-add-booking">
          Przejdź do mojego panelu
        </button>
      </div>
      {helpOpen && (
        <div className="modal-overlay" onClick={() => setHelpOpen(false)} role="presentation">
          <div className="modal-card page-help-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="no-access-help-title">
            <div className="modal-header">
              <div>
                <p className="page-help-kicker">Jak działa ten ekran</p>
                <h2 id="no-access-help-title">{help.title}</h2>
              </div>
              <button type="button" className="btn-close" onClick={() => setHelpOpen(false)} aria-label="Zamknij pomoc">✕</button>
            </div>
            <div className="modal-body page-help-body">
              {help.intro && <p className="page-help-intro">{help.intro}</p>}
              {(help.sections || []).map((section, idx) => (
                <section key={idx} className="page-help-section">
                  <h3>{section.heading}</h3>
                  {section.bullets?.length > 0 && (
                    <ul>{section.bullets.map((bullet, bIdx) => <li key={bIdx}>{bullet}</li>)}</ul>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Eksportuj wszystkie potrzebne komponenty
export {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  Link,
  useNavigate,
  useParams,
  useLocation,
  RequireAuth,
  RequirePermission,
  NoAccess
};